import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, AutocompleteRenderInputParams, Box, Checkbox, Chip, DialogTitle, FormControl, Grid, Icon, IconButton, InputLabel, ListItemText, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CustomDialog from "components/custom/CustomDialog";
import { CustomButton } from "components/roundedbutton/CustomButton";
import dayjs from "dayjs";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import HeaderOuBannerDependendoDaInterpretacaoH1 from "layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1";
import { IAbstractModel } from "model/abstract.model";
import { IEstabelecimento } from "model/estabelecimento.model";
import { useCallback, useState } from "react";
import { useAppSelector } from "redux/hooks";

import { isChecked, isEmpty, toLocalDateBackEndFriendly } from "utils/util";
import { FiltroAvancadoContaReceberDTO, SitacaoCheque, StatusContaPagarReceber, TDataState, TDataStateField, TFiltroAvancadoContasReceberProps } from "./financas.model";
import { theme } from 'theme';


const FiltroAvancadoContasReceber = (props: TFiltroAvancadoContasReceberProps) => {
    const { ESTABELECIMENTOS } = useAppSelector(e => e.token);
    const estabelecimentoSelecionado = useAppSelector(e => e.estabelecimentoSelecionado);

    const initialState = {
        filtroFormaPagamento: "" as string,
        filtroBanco: "" as string,
        filtroSituacaoCheque: "" as string,
        filtroAvancadoContasReceber: {
            descricao: props.filtroAvancado.descricao,
            pessoa: props.filtroAvancado.pessoa,
            numeroDocumentos: props.filtroAvancado.numeroDocumentos,
            numeroParcela: props.filtroAvancado.numeroParcela,
            emissao: props.filtroAvancado.emissao,
            vencimento: props.filtroAvancado.vencimento,
            baixa: props.filtroAvancado.baixa,
            estabelecimentos: props.filtroAvancado.estabelecimentos,
            formasPagamento: props.filtroAvancado.formasPagamento,
            statusParcela: props.filtroAvancado.statusParcela,
            filtroCartaoDTO: props.filtroAvancado.filtroCartaoDTO,
            filtroChequeDTO: props.filtroAvancado.filtroChequeDTO,
            pageNumber: props.filtroAvancado.pageNumber,
            pageSize: props.filtroAvancado.pageSize
        } as FiltroAvancadoContaReceberDTO,
    };

    const [stateLocal, setStateLocal] = useState(initialState);
    const { addError } = useSnackbarCustom();

    const handleFormaPagamentoCheck = useCallback((uuid: string, formasPagamento: Array<IAbstractModel>): void => {
        const found = formasPagamento.find(e => e.uuid === uuid);

        if (found) {
            const formasPagamentoRestantes = formasPagamento.includes(found)
                ? formasPagamento.filter(e => e.uuid !== uuid)
                : [...formasPagamento, found];

            setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    formasPagamento: formasPagamentoRestantes
                }
            }));
        }
        //eslint-disable-next-line
    }, []);

    const handleEstabelecimentoCheck = useCallback((uuid: string, estabelecimentos: Array<IEstabelecimento>): void => {
        const found = estabelecimentos.find(e => e.uuid === uuid);

        if (found) {
            const estabelecimentosRestantes = estabelecimentos.includes(found)
                ? estabelecimentos.filter(e => e.uuid !== uuid)
                : [...estabelecimentos, found];

            if (isEmpty(estabelecimentosRestantes)) {
                return addError({
                    message: "É necessário selecionar ao menos 1 estabelecimento.",
                    id: "filtro_avancado_conta_receber_min_estabelecimento_error",
                    closeable: true,
                    persist: true,
                    preventDuplicate: true
                });
            }

            setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    estabelecimentos: estabelecimentosRestantes
                }
            }));
        }
        //eslint-disable-next-line
    }, []);

    const onFormaPagamentoInputChange = useCallback((event: React.SyntheticEvent<Element, Event>, value: string) => {
        if (event && event.type === "click") {
            return setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    formasPagamento: []
                }
            }));
        }
        return setStateLocal(prevState => ({ ...prevState, filtroFormaPagamento: value }));
    }, []);

    const filterFormasPagamento = useCallback((formasPagamento: Array<IAbstractModel>, filtro: string) => {
        if (!filtro) return formasPagamento;
        return formasPagamento.filter(e => e.descritivo.toUpperCase().includes(filtro.toUpperCase()));
    }, []);

    const filterBancos = useCallback((bancos: Array<IAbstractModel>, filter: string) => {
        if (!filter) return bancos;
        return bancos.filter(e => e.descritivo.toUpperCase().includes(filter.toUpperCase()));
    }, []);

    const renderInputGeneric = useCallback((params: AutocompleteRenderInputParams, label: string) => {
        return (
            <TextField
                {...params}
                id={`render-${label}-autocomplete`}
                fullWidth={true}
                variant="standard"
                label={label}
            />
        );
    }, []);

    const onRemoveNumeroDocumento = useCallback((numeroDocumento: string) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                numeroDocumentos: prevState.filtroAvancadoContasReceber.numeroDocumentos.filter(e => e !== numeroDocumento)
            }
        }));
    }, []);

    const renderTagsNumeroDocumento = useCallback((numerosDocumentos: Array<string>) => {
        return (
            numerosDocumentos.map((numeroDocumento) => (
                <Box
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.default,
                        borderRadius: '50px',
                        marginRight: '5px',
                        marginTop: '2px',
                        marginBottom: '2px'
                    }}
                >
                    <Chip
                        label={numeroDocumento}
                        key={numeroDocumento}
                        sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default }}

                    />
                    <IconButton
                        onClick={() => onRemoveNumeroDocumento(numeroDocumento)}
                        sx={{
                            color: theme.palette.background.default,
                            padding: 0,
                            paddingRight: "2px",
                            fontSize: '15px'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))
        );
        //eslint-disable-next-line
    }, []);

    const renderTagsFormaPagamento = useCallback((formasPagamento: Array<IAbstractModel>) => {
        return (
            formasPagamento.map(fp => {
                return (
                    <Box
                        sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, borderRadius: '50px', marginRight: '5px', marginTop: '2px', marginBottom: '2px' }}
                    >
                        <Chip
                            key={fp.uuid}
                            sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, width: 220 }}
                            label={fp.descritivo}
                        />
                        <IconButton
                            onClick={() => handleFormaPagamentoCheck(fp.uuid, formasPagamento)}
                            sx={{
                                color: theme.palette.background.default,
                                padding: 0,
                                paddingRight: "2px",
                                fontSize: '15px'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                );
            })
        );
        //eslint-disable-next-line
    }, []);


    const renderTagsEstabelecimentos = useCallback((estabelecimentos: Array<IEstabelecimento>) => {
        return (
            estabelecimentos.map(estabelecimento => {
                return (
                    <Box
                        sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, borderRadius: '50px', marginRight: '5px', marginTop: '2px', marginBottom: '2px' }}
                    >
                        <Chip
                            key={estabelecimento.uuid}
                            sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, width: 220 }}
                            label={estabelecimento.descritivo}
                        />
                        <IconButton
                            onClick={() => handleEstabelecimentoCheck(estabelecimento.uuid, estabelecimentos)}
                            sx={{
                                color: theme.palette.background.default,
                                padding: 0,
                                paddingRight: "2px",
                                fontSize: '15px'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                );
            })
        );
        //eslint-disable-next-line
    }, []);

    const onToggleSituacaoCheque = useCallback((arr: Array<SitacaoCheque>, situacaoCheque: SitacaoCheque) => {
        const isItemInArray = isChecked(arr, situacaoCheque);

        const situacaoChequeArr = isItemInArray
            ? arr.filter(e => e !== situacaoCheque)
            : [...arr, situacaoCheque];

        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroChequeDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                    situacoesCheque: situacaoChequeArr
                }
            }
        }));
        //eslint-disable-next-line
    }, []);

    const renderOptionSituacaoCheque = (situacaoCheque: SitacaoCheque) => {
        return (
            <MenuItem
                key={situacaoCheque}
                value={situacaoCheque}
                onClick={() => onToggleSituacaoCheque(stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.situacoesCheque, situacaoCheque)}
            >
                <Checkbox
                    id={`item-${situacaoCheque}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main
                        }
                    }}
                    checked={isChecked(stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.situacoesCheque, situacaoCheque)}
                />
                <ListItemText primary={situacaoCheque} />
            </MenuItem>
        );
    };

    const handleSituacaoChequeCheck = useCallback((situacaoCheque: SitacaoCheque, situacoesCheque: Array<SitacaoCheque>) => {
        const found = situacoesCheque.find(e => e === situacaoCheque);

        if (found) {
            const situacoesRestantes = situacoesCheque.includes(found)
                ? situacoesCheque.filter(e => e !== situacaoCheque)
                : [...situacoesCheque, found];

            setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    filtroChequeDTO: {
                        ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                        situacoesCheque: situacoesRestantes
                    }
                }
            }));
        }
    }, []);

    const renderTagsSituacaoCheque = (situacoesCheque: Array<SitacaoCheque>) => {
        return (
            situacoesCheque.map(sc => {
                return (
                    <Box
                        sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, borderRadius: '50px', marginRight: '5px', marginTop: '2px', marginBottom: '2px' }}
                    >
                        <Chip
                            key={sc}
                            sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, width: 220 }}
                            label={sc}
                        />
                        <IconButton
                            onClick={() => handleSituacaoChequeCheck(sc, situacoesCheque)}
                            sx={{
                                color: theme.palette.background.default,
                                padding: 0,
                                paddingRight: "2px",
                                fontSize: '15px'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                );
            })
        );
    };

    const onAddNsu = useCallback((value: Array<string>) => {
        setStateLocal(prevState => ({
            ...prevState, filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroCartaoDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroCartaoDTO,
                    nsus: value
                }
            }
        }));
    }, []);

    const onRemoveNSU = useCallback((nsu: string) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroCartaoDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroCartaoDTO,
                    nsus: prevState.filtroAvancadoContasReceber.filtroCartaoDTO.nsus.filter(e => e !== nsu)
                }
            }
        }));
    }, []);


    const renderTagsNSU = useCallback((NSUs: Array<string>) => {
        return (
            NSUs.map((nsu) => (
                <Box
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, borderRadius: '50px', marginRight: '5px', marginTop: '2px', marginBottom: '2px' }}
                >
                    <Chip
                        label={nsu}
                        key={nsu}
                        sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default }}

                    />
                    <IconButton
                        onClick={() => onRemoveNSU(nsu)}
                        sx={{
                            color: theme.palette.background.default,
                            padding: 0,
                            paddingRight: "2px",
                            fontSize: '15px'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))
        );
        //eslint-disable-next-line
    }, []);


    const onAddNumeroControle = useCallback((value: Array<string>) => {
        setStateLocal(prevState => ({
            ...prevState, filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroCartaoDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroCartaoDTO,
                    numerosControle: value
                }
            }
        }));
    }, []);

    const onRemoveNumeroControle = (numeroControle: string) => {
        setStateLocal(prevState => ({
            ...prevState, filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroCartaoDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroCartaoDTO,
                    numerosControle: prevState.filtroAvancadoContasReceber.filtroCartaoDTO.numerosControle.filter(e => e !== numeroControle)
                }
            }
        }));
    };

    const renderTagsNumeroControle = (numerosControle: Array<string>) => {
        return (
            numerosControle.map((numeroControle) => (
                <Box
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.background.default, borderRadius: '50px', marginRight: '5px', marginTop: '2px', marginBottom: '2px' }}
                >
                    <Chip
                        label={numeroControle}
                        key={numeroControle}
                        sx={{ height: '1.5rem', backgroundColor: theme.palette.primary.main, color: theme.palette.background.default }}

                    />
                    <IconButton
                        onClick={() => onRemoveNumeroControle(numeroControle)}
                        sx={{
                            color: theme.palette.background.default,
                            padding: 0,
                            paddingRight: "2px",
                            fontSize: '15px'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))
        );
    };

    const renderPessoaOptions = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: IAbstractModel) => {
        return (
            <li {...props} key={option.uuid} >
                {option.descritivo}
            </li >
        );
        //eslint-disable-next-line
    }, []);

    const renderBancoOptions = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: IAbstractModel) => {
        return (
            <li {...props} key={option.uuid} >
                {option.descritivo}
            </li >
        );
        //eslint-disable-next-line
    }, []);

    const renderOptionFormaPagamento = useCallback((formaPagamento: IAbstractModel) => {
        return (
            <MenuItem
                key={formaPagamento.uuid}
                value={formaPagamento.uuid}
                onClick={() => onToggleFormaPagamento(stateLocal.filtroAvancadoContasReceber.formasPagamento, formaPagamento)}
            >
                <Checkbox
                    id={`item-${formaPagamento.descritivo}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main
                        }
                    }}
                    checked={isChecked(stateLocal.filtroAvancadoContasReceber.formasPagamento, formaPagamento)}
                />
                <ListItemText primary={formaPagamento.descritivo} />
            </MenuItem>
        );
        //eslint-disable-next-line
    }, [stateLocal.filtroAvancadoContasReceber.formasPagamento]);

    const renderOptionsEstabelecimentos = useCallback((estabelecimento: IEstabelecimento) => {
        return (
            <MenuItem
                key={estabelecimento.uuid}
                value={estabelecimento.uuid}
                onClick={() => onToggleEstabelecimento(stateLocal.filtroAvancadoContasReceber.estabelecimentos, estabelecimento)}
            >
                <Checkbox
                    id={`item-${estabelecimento.descritivo}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main
                        }
                    }}
                    checked={isChecked(stateLocal.filtroAvancadoContasReceber.estabelecimentos, estabelecimento)}
                />
                <ListItemText primary={estabelecimento.descritivo} />
            </MenuItem>
        );
        //eslint-disable-next-line
    }, [stateLocal.filtroAvancadoContasReceber.estabelecimentos]);

    const onChangeFiltroDescricao = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        return setStateLocal(prevState => ({
            ...prevState, filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                descricao: e.target.value
            }
        }));
        //eslint-disable-next-line
    }, []);

    const onSelectPessoa = useCallback((pessoa: IAbstractModel | null) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                pessoa: pessoa
            }
        }));
    }, []);

    const onAddNumeroDocumento = useCallback((value: Array<string>) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                numeroDocumentos: value
            }
        }));
    }, []);

    const onChangeFiltroParcela = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        return setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                numeroParcela: e.target.value
            }
        }));
    }, []);

    const onChangeDate = useCallback((e: any, state: TDataState, campo: TDataStateField) => {
        if (!e?.isValid()) {
            return setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    emissao: {
                        ...prevState.filtroAvancadoContasReceber.emissao,
                        inicio: (state === TDataState.EMISSAO && campo === TDataStateField.INICIO) ? null : prevState.filtroAvancadoContasReceber.emissao.inicio,
                        fim: (state === TDataState.EMISSAO && campo === TDataStateField.FIM) ? null : prevState.filtroAvancadoContasReceber.emissao.fim
                    },
                    vencimento: {
                        ...prevState.filtroAvancadoContasReceber.vencimento,
                        inicio: (state === TDataState.VENCIMENTO && campo === TDataStateField.INICIO) ? null : prevState.filtroAvancadoContasReceber.vencimento.inicio,
                        fim: (state === TDataState.VENCIMENTO && campo === TDataStateField.FIM) ? null : prevState.filtroAvancadoContasReceber.vencimento.fim
                    },
                    baixa: {
                        ...prevState.filtroAvancadoContasReceber.baixa,
                        inicio: (state === TDataState.BAIXA && campo === TDataStateField.INICIO) ? null : prevState.filtroAvancadoContasReceber.baixa.inicio,
                        fim: (state === TDataState.BAIXA && campo === TDataStateField.FIM) ? null : prevState.filtroAvancadoContasReceber.baixa.fim
                    }
                }
            }));
        }

        const novaData = toLocalDateBackEndFriendly(e.toDate());
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                emissao: {
                    ...prevState.filtroAvancadoContasReceber.emissao,
                    inicio: (state === TDataState.EMISSAO && campo === TDataStateField.INICIO) ? novaData : prevState.filtroAvancadoContasReceber.emissao.inicio,
                    fim: (state === TDataState.EMISSAO && campo === TDataStateField.FIM) ? novaData : prevState.filtroAvancadoContasReceber.emissao.fim
                },
                vencimento: {
                    ...prevState.filtroAvancadoContasReceber.vencimento,
                    inicio: (state === TDataState.VENCIMENTO && campo === TDataStateField.INICIO) ? novaData : prevState.filtroAvancadoContasReceber.vencimento.inicio,
                    fim: (state === TDataState.VENCIMENTO && campo === TDataStateField.FIM) ? novaData : prevState.filtroAvancadoContasReceber.vencimento.fim
                },
                baixa: {
                    ...prevState.filtroAvancadoContasReceber.baixa,
                    inicio: (state === TDataState.BAIXA && campo === TDataStateField.INICIO) ? novaData : prevState.filtroAvancadoContasReceber.baixa.inicio,
                    fim: (state === TDataState.BAIXA && campo === TDataStateField.FIM) ? novaData : prevState.filtroAvancadoContasReceber.baixa.fim
                }
            }
        }));
    }, []);

    const onToggleFormaPagamento = useCallback((formasPagamento: Array<IAbstractModel>, formaPagamento: IAbstractModel) => {
        const isItemInArray = isChecked(formasPagamento, formaPagamento);

        const formasPagamentoRestante = isItemInArray
            ? formasPagamento.filter(e => e.uuid !== formaPagamento.uuid)
            : [...formasPagamento, formaPagamento];

        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                formasPagamento: formasPagamentoRestante
            }
        }));
        //eslint-disable-next-line
    }, []);

    const onToggleEstabelecimento = useCallback((estabelecimentos: Array<IEstabelecimento>, estabelcimento: IEstabelecimento) => {
        const isItemInArray = isChecked(estabelecimentos, estabelcimento);

        const estabelecimentosRestantes = isItemInArray
            ? estabelecimentos.filter(e => e.uuid !== estabelcimento.uuid)
            : [...estabelecimentos, estabelcimento];

        if (isEmpty(estabelecimentosRestantes)) {
            return addError({
                message: "É necessário selecionar ao menos 1 estabelecimento.",
                id: "filtro_avancado_conta_receber_min_estabelecimento_error",
                closeable: true,
                persist: true,
                preventDuplicate: true
            });
        }

        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                estabelecimentos: estabelecimentosRestantes
            }
        }));
        //eslint-disable-next-line
    }, []);



    const onClickStatus = useCallback((array: Array<StatusContaPagarReceber>, value: StatusContaPagarReceber) => {
        const isItemInArray = isChecked(array, value);

        const status = isItemInArray
            ? array.filter(e => e !== value)
            : [...array, value];

        if (isEmpty(status)) return addError({
            message: " O campo status deve ter pelo menos 1 valor selecionado.",
            id: "status-vazio-error",
            preventDuplicate: true,
            closeable: true
        });

        setStateLocal(prevState => ({
            ...prevState, filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                statusParcela: status
            }
        }));
        //eslint-disable-next-line
    }, []);

    const onSelectBanco = useCallback((banco: IAbstractModel | null) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroChequeDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                    banco: banco
                }
            }
        }));
    }, []);

    const onChangeAgenciaOuNumeroCheque = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, state: string) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroChequeDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                    [state]: event.target.value !== "" ? event.target.value : null
                }
            }
        }));
    };

    const getSituacoesChequeFilter = useCallback((situacoesCheque: Array<SitacaoCheque>, filtro: string) => {
        if (!filtro) return situacoesCheque;
        return situacoesCheque.filter(e => e.toUpperCase().includes(filtro.toUpperCase()));
    }, []);

    const onInputChangeSituacaoCheque = useCallback((event: React.SyntheticEvent, value: string) => {
        if (event && event.type === "click") {
            return setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    filtroChequeDTO: {
                        ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                        situacoesCheque: []
                    }
                }
            }));
        }
        return setStateLocal(prevState => ({
            ...prevState,
            filtroSituacaoCheque: value
        }));
    }, []);


    const onSelectChequeComTerceiro = useCallback((chequeComTerceiro: IAbstractModel | null) => {
        setStateLocal(prevState => ({
            ...prevState,
            filtroAvancadoContasReceber: {
                ...prevState.filtroAvancadoContasReceber,
                filtroChequeDTO: {
                    ...prevState.filtroAvancadoContasReceber.filtroChequeDTO,
                    chequeComTerceiro: chequeComTerceiro
                }
            }
        }));
    }, []);


    const onInputChangeEstabelecimentos = useCallback((event: React.SyntheticEvent<Element, Event>, value: string) => {
        if (event && event.type === "click") {
            return setStateLocal(prevState => ({
                ...prevState,
                filtroAvancadoContasReceber: {
                    ...prevState.filtroAvancadoContasReceber,
                    estabelecimentos: [estabelecimentoSelecionado]
                }
            }));
        }
        return setStateLocal(prevState => ({ ...prevState, filtroFormaPagamento: value }));
        //eslint-disable-next-line
    }, [estabelecimentoSelecionado.uuid]);

    return (
        <CustomDialog
            open
            fullWidth
            maxWidth={'xl'}
            onClose={props.onClose}
        >
            <DialogTitle
                id="dialog-title"
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'transparent`'
                }}
            >
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='baixar-conta-receber'
                    iconClassName='fa-solid fa-filter'
                    label='Filtro Avançado'
                    maxHeight="35px"
                />
            </DialogTitle>

            <Box
                sx={{
                    overflowY: 'auto',
                    width: "100%"
                }}
            >
                <Grid container>
                    <Grid container sx={{ marginTop: "20px" }}>
                        <Grid item xs={6}>
                            <TextField variant="standard" sx={{ width: '90%', }}
                                inputProps={{ maxLength: 200, id: "filtro-descricao-nome-numero" }}
                                value={stateLocal.filtroAvancadoContasReceber.descricao}
                                onChange={(e) => onChangeFiltroDescricao(e)}
                                label="Descrição, nome do cliente ou número do documento"
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Autocomplete sx={{ width: "90%" }}
                                value={stateLocal.filtroAvancadoContasReceber.pessoa}
                                id="Filtro-avancado-contas-receber-pessoa-ac"
                                options={props.filtroAvancadoOptions.pessoa}
                                getOptionLabel={(option) => option.descritivo}
                                onInputChange={(_, value) => props.onInputChangePessoa(value)}
                                filterOptions={(x) => x}
                                noOptionsText={'Nenhum resultado encontrado'}
                                onChange={(e, value) => onSelectPessoa(value)}
                                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                                renderOption={(props, option) => renderPessoaOptions(props, option)}
                                renderInput={(params) => renderInputGeneric(params, "Pessoa")}
                                onBlur={props.onResetFiltroPessoa}
                            />
                        </Grid>
                    </Grid>

                    <Grid container sx={{ marginTop: "20px" }}>
                        <Grid item xs={6}>
                            <Tooltip
                                title="Digite o número do documento e aperte enter"
                                arrow
                                followCursor
                            >
                                <FormControl fullWidth variant="standard" error={false}>
                                    <Autocomplete sx={{ width: "90%" }}
                                        options={[]}
                                        freeSolo
                                        multiple
                                        clearIcon={false}
                                        value={stateLocal.filtroAvancadoContasReceber.numeroDocumentos}
                                        renderTags={(value) => renderTagsNumeroDocumento(value)}
                                        onChange={(_, value) => onAddNumeroDocumento(value)}
                                        renderInput={(params) => renderInputGeneric(params, "Número do(s) documento(s)")}
                                    />
                                </FormControl>
                            </Tooltip>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField variant="standard"
                                sx={{ width: "90%" }}
                                inputProps={{ maxLength: 200, id: "filtro-parcela" }}
                                value={stateLocal.filtroAvancadoContasReceber.numeroParcela}
                                onChange={onChangeFiltroParcela}
                                label="Parcela"
                            />
                        </Grid>
                    </Grid>

                    <Grid container sx={{ marginTop: "20px" }}>
                        <Grid item xs={5} sx={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }} gap={2}>
                            <Typography sx={{
                                fontSize: "20px",
                                textAlign: "center",
                                alignSelf: "center",
                                width: "33%"
                            }}>
                                Emissão
                            </Typography>

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',
                                        onChange: (e: any) => onChangeDate(e, TDataState.EMISSAO, TDataStateField.INICIO),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Inicio"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.emissao.inicio ? dayjs(props.filtroAvancado.emissao.inicio) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.EMISSAO, TDataStateField.INICIO)}
                            />

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',

                                        onChange: (e: any) => onChangeDate(e, TDataState.EMISSAO, TDataStateField.FIM),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Fim"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.emissao.fim ? dayjs(props.filtroAvancado.emissao.fim) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.EMISSAO, TDataStateField.FIM)}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={5}
                            gap={2}
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-around"
                            }}

                        >
                            <Typography sx={{
                                fontSize: "20px",
                                textAlign: "center",
                                alignSelf: "center",
                                width: "33%"
                            }}>
                                Vencimento
                            </Typography>

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',
                                        onChange: (e: any) => onChangeDate(e, TDataState.VENCIMENTO, TDataStateField.INICIO),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Inicio"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.vencimento.inicio ? dayjs(props.filtroAvancado.vencimento.inicio) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.VENCIMENTO, TDataStateField.INICIO)}
                            />

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',
                                        onChange: (e: any) => onChangeDate(e, TDataState.VENCIMENTO, TDataStateField.FIM),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Fim"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.vencimento.fim ? dayjs(props.filtroAvancado.vencimento.fim) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.VENCIMENTO, TDataStateField.FIM)}
                            />
                        </Grid>

                        <Grid item xs={1} />

                    </Grid>

                    <Grid container sx={{ marginTop: "20px" }}>
                        <Grid item xs={5} sx={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }} gap={2}>
                            <Typography sx={{
                                fontSize: "20px",
                                textAlign: "center",
                                alignSelf: "center",
                                width: "33%",
                            }}>
                                Baixa
                            </Typography>

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',
                                        onChange: (e: any) => onChangeDate(e, TDataState.BAIXA, TDataStateField.INICIO),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Inicio"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.baixa.inicio ? dayjs(props.filtroAvancado.baixa.inicio) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.BAIXA, TDataStateField.INICIO)}
                            />

                            <DatePicker
                                slotProps={{
                                    textField: {
                                        id: `data_inicio`,
                                        variant: 'standard',
                                        onChange: (e: any) => onChangeDate(e, TDataState.BAIXA, TDataStateField.FIM),
                                        sx: { width: "33.3%" }
                                    }
                                }}
                                label="Fim"
                                format='DD/MM/YYYY'
                                value={props.filtroAvancado.baixa.fim ? dayjs(props.filtroAvancado.baixa.fim) : null}
                                onChange={(e: any) => onChangeDate(e, TDataState.BAIXA, TDataStateField.FIM)}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={6}>
                            {ESTABELECIMENTOS.length > 1 &&
                                <Autocomplete sx={{ marginTop: "7px", width: "90%" }}
                                    value={stateLocal.filtroAvancadoContasReceber.estabelecimentos}
                                    id="Filtro-avancado-contas-receber-estabelecimentos-ac"
                                    multiple
                                    options={ESTABELECIMENTOS}
                                    getOptionLabel={(option: IAbstractModel) => option.descritivo}
                                    onInputChange={(event, value) => onInputChangeEstabelecimentos(event, value)}
                                    filterOptions={(x) => x}
                                    noOptionsText={'Nenhum resultado encontrado'}

                                    isOptionEqualToValue={(option: IAbstractModel, value: IAbstractModel) => option.uuid === value.uuid}
                                    renderInput={(params: any) => (
                                        <TextField
                                            {...params}
                                            label="Estabelecimentos"
                                            variant="standard"
                                        />
                                    )}
                                    renderTags={renderTagsEstabelecimentos}
                                    renderOption={(_, option) => renderOptionsEstabelecimentos(option)}
                                />
                            }
                        </Grid>
                    </Grid>

                    <Grid container sx={{ marginTop: "20px" }}>
                        <Grid item xs={6}>
                            <Autocomplete sx={{ marginTop: "7px", width: "90%" }}
                                value={stateLocal.filtroAvancadoContasReceber.formasPagamento}
                                id="Filtro-avancado-contas-receber-formas-pagamento-ac"
                                multiple
                                options={filterFormasPagamento(props.formasPagamento, stateLocal.filtroFormaPagamento)}
                                getOptionLabel={(option: IAbstractModel) => option.descritivo}
                                onInputChange={(event, value) => onFormaPagamentoInputChange(event, value)}
                                filterOptions={(x) => x}
                                noOptionsText={'Nenhum resultado encontrado'}

                                isOptionEqualToValue={(option: IAbstractModel, value: IAbstractModel) => option.uuid === value.uuid}
                                renderInput={(params: any) => (
                                    <TextField
                                        {...params}
                                        label="Formas de pagamento"
                                        variant="standard"
                                    />
                                )}
                                renderTags={renderTagsFormaPagamento}
                                renderOption={(_, option) => renderOptionFormaPagamento(option)}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <InputLabel
                                id="formas-comissao-label"
                                htmlFor="formas-comissao-label"
                            >
                                Status
                            </InputLabel>
                            <Select sx={{ width: "90%" }}
                                variant='standard'
                                id="formas-comissao-component"
                                multiple
                                inputProps={{ id: "formas-comissao-label" }}
                                value={stateLocal.filtroAvancadoContasReceber.statusParcela}

                                renderValue={(selected) => (
                                    <>
                                        {selected.map((value: StatusContaPagarReceber) => {
                                            return (<Chip
                                                key={value}
                                                sx={{
                                                    height: '1.2rem',
                                                    marginRight: '2px',
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.background.default,
                                                    paddingLeft: "10px",
                                                    paddingRight: "10px"
                                                }}
                                                label={value}
                                            />
                                            );
                                        })}
                                    </>
                                )}
                                MenuProps={{ PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 100 } } }}
                            >
                                {props.filtroAvancadoOptions.status.map(p => (
                                    <MenuItem
                                        key={p}
                                        value={p}
                                        onClick={() => onClickStatus(stateLocal.filtroAvancadoContasReceber.statusParcela, p)}
                                    >
                                        <Checkbox
                                            id={`item-${p}-input`}
                                            sx={{
                                                '&.MuiCheckbox-root.Mui-checked': {
                                                    color: theme.palette.primary.main
                                                }
                                            }}
                                            checked={stateLocal.filtroAvancadoContasReceber.statusParcela.includes(p)}
                                        />
                                        <ListItemText primary={p} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>

                    <Grid container xs={12}>
                        <Grid item xs={11.5} sx={{
                            display: "flex",
                            flexDirection: "row",
                            marginTop: "20px",
                            alignItems: "center",
                            borderBottom: `1px solid ${theme.palette.text.disabled}`,
                            width: "90%"

                        }}>
                            <Icon className="fa-solid fa-credit-card" sx={{ width: "30px" }} />
                            <DialogTitle
                                id="filtro-avancado-contas-receber-filtro-cartoes-title"
                            >
                                Filtros de cartões
                            </DialogTitle>
                        </Grid>
                        <Grid container sx={{ marginTop: "20px" }}>
                            <Grid item xs={0.5} />
                            <Grid item xs={5.5}>
                                <Tooltip
                                    title="Digite o NSU e aperte enter"
                                    arrow
                                    followCursor
                                >
                                    <FormControl fullWidth variant="standard" error={false}>
                                        <Autocomplete sx={{ width: "90%" }}
                                            options={[]}
                                            freeSolo
                                            multiple
                                            clearIcon={false}
                                            value={stateLocal.filtroAvancadoContasReceber.filtroCartaoDTO.nsus}
                                            renderTags={(value) => renderTagsNSU(value)}
                                            onChange={(_, value) => onAddNsu(value)}
                                            renderInput={(params) => renderInputGeneric(params, "NSU(s)")}
                                        />
                                    </FormControl>
                                </Tooltip>
                            </Grid>

                            <Grid item xs={5.5}>
                                <Tooltip
                                    title="Digite o NSU e aperte enter"
                                    arrow
                                    followCursor
                                >
                                    <FormControl fullWidth variant="standard" error={false}>
                                        <Autocomplete sx={{ width: "90%" }}
                                            options={[]}
                                            freeSolo
                                            multiple
                                            clearIcon={false}
                                            value={stateLocal.filtroAvancadoContasReceber.filtroCartaoDTO.numerosControle}
                                            renderTags={(value) => renderTagsNumeroControle(value)}
                                            onChange={(_, value) => onAddNumeroControle(value)}
                                            renderInput={(params) => renderInputGeneric(params, "Número de controle(s)")}
                                        />
                                    </FormControl>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={0.5} />
                        </Grid>
                    </Grid>

                    <Grid container xs={12}>
                        <Grid item xs={11.5} sx={{
                            display: "flex",
                            flexDirection: "row",
                            marginTop: "20px",
                            alignItems: "center",
                            borderBottom: `1px solid ${theme.palette.text.disabled}`,
                            width: "90%"

                        }}>
                            <Icon className="fa-solid fa-money-check-dollar" sx={{ width: "30px" }} />
                            <DialogTitle
                                id="filtro-avancado-contas-receber-filtro-cheques-title"
                            >
                                Filtros de cheque
                            </DialogTitle>
                        </Grid>
                        <Grid container sx={{ marginTop: "20px" }}>
                            <Grid container>
                                <Grid item xs={0.5} />
                                <Grid item xs={5.5}>
                                    <Autocomplete sx={{ width: "90%" }}
                                        value={stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.banco}
                                        id="Filtro-avancado-contas-receber-banco-ac"
                                        options={filterBancos(props.filtroAvancadoOptions.bancos, stateLocal.filtroBanco)}
                                        getOptionLabel={(option) => option.descritivo}
                                        onInputChange={(_, value) => setStateLocal(prevState => ({ ...prevState, filtroBanco: value }))}
                                        filterOptions={(x) => x}
                                        noOptionsText={'Nenhum resultado encontrado'}
                                        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                                        onChange={(e, value) => onSelectBanco(value)}
                                        renderOption={(props, option) => renderBancoOptions(props, option)}
                                        renderInput={(params) => renderInputGeneric(params, "Banco")}
                                    />
                                </Grid>

                                <Grid item xs={5.5}>
                                    <TextField variant="standard"
                                        sx={{ marginLeft: "15px", width: "90%" }}
                                        inputProps={{ maxLength: 200, id: "filtro-parcela" }}
                                        value={stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.agenciaCheque}
                                        onChange={(event) => onChangeAgenciaOuNumeroCheque(event, "agenciaCheque")}
                                        label="Agência do cheque"
                                    />
                                </Grid>
                                <Grid item xs={0.5} />
                            </Grid>

                            <Grid container sx={{ marginTop: "20px" }}>
                                <Grid item xs={0.5} />
                                <Grid item xs={5.5}>
                                    <TextField variant="standard" sx={{ width: '90%', }}
                                        inputProps={{ maxLength: 200, id: "filtro-descricao-nome-numero" }}
                                        value={stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.numeroCheque}
                                        onChange={(event) => onChangeAgenciaOuNumeroCheque(event, "numeroCheque")}
                                        label="Número do cheque"
                                    />
                                </Grid>

                                <Grid item xs={5.5}>
                                    <Autocomplete sx={{ marginTop: "7px", width: "90%" }}
                                        value={stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.situacoesCheque}
                                        id="Filtro-avancado-contas-receber-situacoes-cheque-ac"
                                        multiple
                                        options={getSituacoesChequeFilter(Object.values(SitacaoCheque), stateLocal.filtroSituacaoCheque)}
                                        getOptionLabel={(option: any) => option}
                                        onInputChange={onInputChangeSituacaoCheque}
                                        // onInputChange={(_, value) => setStateLocal(prevState => ({ ...prevState, filtroFormaPagamento: value }))}
                                        filterOptions={(x) => x}
                                        noOptionsText={'Nenhum resultado encontrado'}

                                        isOptionEqualToValue={(option, value) => option === value}
                                        renderInput={(params) => renderInputGeneric(params, "Situações de cheque")}
                                        renderTags={renderTagsSituacaoCheque}
                                        renderOption={(_, option) => renderOptionSituacaoCheque(option)}
                                    />

                                </Grid>
                                <Grid item xs={0.5} />
                            </Grid>

                            <Grid container sx={{ marginTop: "20px" }}>
                                <Grid item xs={0.5} />
                                <Grid item xs={5.5}>

                                    <Autocomplete sx={{ width: "90%" }}
                                        value={stateLocal.filtroAvancadoContasReceber.filtroChequeDTO.chequeComTerceiro}
                                        id="Filtro-avancado-contas-cheque-terceiro-ac"
                                        options={props.filtroAvancadoOptions.pessoa}
                                        getOptionLabel={(option) => option.descritivo}
                                        onInputChange={(_, value) => props.onInputChangePessoa(value)}
                                        filterOptions={(x) => x}
                                        noOptionsText={'Nenhum resultado encontrado'}
                                        onChange={(e, value) => onSelectChequeComTerceiro(value)}
                                        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                                        renderOption={(props, option) => renderPessoaOptions(props, option)}
                                        renderInput={(params) => renderInputGeneric(params, "Cheque com terceiro")}
                                        onBlur={props.onResetFiltroPessoa}
                                    />
                                    <div style={{ height: `50px` }} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 2,
                    backgroundColor: 'white',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10,
                    marginTop: "20px"
                }}
            >
                <CustomButton
                    sx={{ minWidth: '80px' }}
                    id="voltar-configuracao-vale-presente-btn"
                    onClick={props.onClose}
                    variant="contained"
                    color="error"
                    startIcon={<Icon className='fa-solid fa-arrow-left' />}
                >
                    Voltar
                </CustomButton>

                <CustomButton
                    sx={{ minWidth: '80px' }}
                    id="ir-para-pdv-btn"
                    onClick={() => props.onConsultar(stateLocal.filtroAvancadoContasReceber)}
                    // disabled={!caixaAberto}
                    variant="contained"
                    color="info"
                // startIcon={<Icon className='fa-solid fa-check' />}
                >
                    Consultar
                </CustomButton>
            </Box>
        </CustomDialog>
    );
};

export default FiltroAvancadoContasReceber;
